---
title: Quotes and the record page
summary: "The register lists every record (quotes, orders), and a record's page shows its process, its tasks, its quote versions and everything known about it."
visibility: authenticated
feature: workflow_engine
audience: end-user
section: Pages
order: 40
last_verified: 2026-10-30
owner: docs
routes: ['/orders']
aliases: [quotes, quote, orders, order, records, record, jobs, job list, register, enquiries, requests, deals]
tour:
  - anchor: records.toolbar.views
    text: "Register lists every record; My tasks lists the records waiting on you."
  - anchor: records.toolbar.filters
    text: "Filters narrows the list by current step, status and queue. Each filter on shows as a chip you can remove."
  - anchor: records.register.table
    text: "Press a column header to sort by it, and drag its edge to resize it. Press a row to open the record."
  - anchor: records.header.new-quote
    text: "New quote record creates a quote from a pasted enquiry, with no email needed."
    when_missing: "No quote process is set up on this deployment."
  - anchor: records.record.process
    text: "The process band shows every step of the record's process and where it stands now. Press a step to read it."
    when_missing: "No record is open, or this record runs no process."
  - anchor: records.record.quotes
    text: "Quote versions is where a quote is prepared, sent and answered."
    when_missing: "No record is open, or no quote has been reached yet."
---

# Quotes and the record page

A record is one piece of work: a quote request and everything that follows it, or an
order. The page carries the deployment's own word for a record, so it may read
**Quotes** or Orders. The register lists them; press one to open its record page.

## The register

- **Register** and **My tasks** switch between every record and the ones waiting on you.
- Search finds a record by its number, customer or a station code (`HKG`).
- **Filters** opens the filter panel: CURRENT STEP (grouped by seat), STATUS, QUEUE.
  Each filter on shows as a chip above the table; **Clear all** removes them.
- **Priority** sorts by the queue's priority; otherwise press a column header to sort by it.
- **Show pricing** switches to the pricing view of the same rows, when you may see prices.

## Create a quote record

Press **New quote record**. **Paste the inquiry** takes the customer's text, and
**Extract fields** fills the intake from it; check the fields, then **Create record**.
A quote also arrives from a triaged email: see [Inbox and triage](/help/pages/inbox).

## The record page

The strip at the top names the record, its customer and route, its current step and
who it is waiting on. Below it:

- **The process band**: the record's process as a map (or **List**). The current step is
  marked; a done step is faded; press any step to read what it needs and produces. On a
  record not started yet, the band offers the start button (for example
  **Start Charter quote**). In the **List** view each earlier run of steps is one line you
  can open (**Show**), the moves back between runs (a re-open, a revision, a step sent
  back) come with their reasons, and the steps around now stay in view. After you finish a
  task from the record, it reopens with the band in view and the step that is now current
  marked for a moment.
- **Tasks**: the record's open tasks, with your own current task first; open one to work it.
  **Completed tasks** lists what was done, and **Re-open** sends a step back.
- **Intake**: what the customer asked for. **Edit intake** changes it; once the process
  runs, every change is a revision the process takes in. Under the requesting company and
  the contractual partner, **Pick a company…** links the field to a company on file, or a
  suggested one links in one press. A link is not a change to the intake: it records no
  revision and never moves the process.
- **Original enquiry** opens the email or text the record was born from.
- **Outcome** closes the record (won, sold, declined and so on); **External references**
  holds the record's numbers in other systems.

## Prepare and send the quote (step 12)

Step 12 of the quote process waits for a quote to be sent, and it is worked on the
record, not on a task form:

1. In **Quote versions**, press **Prepare quote**, fill the amount and how long it is valid,
   and press **Prepare**. The quotation PDF is rendered, and an offer email is drafted on
   the record's email thread.
2. **View PDF** opens the quotation in the app to check it; **Download PDF** saves it; **Edit** changes the figures while it is not sent.
3. **Open the email draft** goes to the thread, where you review and send the email.
   If there is no email thread, send the PDF your own way and press
   **Mark as sent another way**.

Sending ends step 12; the record moves to step 13.

## Record the customer's answer (step 13)

Step 13 waits for the customer. In **Quote versions** record what they said:
**Accept**, **Counter** (with their counter amount, which sends the price back to be
proposed again), **Decline**, or **Requote**. A quote whose validity runs out raises a
task for the Commercial seat. A customer's email that reads as their answer waits here; press its record button to fill the answer in, or **Dismiss** it.

## Close or reopen a record

**Outcome** closes a record. A record closed as not an enquiry can be brought back with
**Reinstate Quote**, which restores where its process stood.
